/**
 * dealThreadPoll.ts — the open deal thread, kept fresh without a reload.
 *
 * installDealThreadLoader reads a thread ONCE and never again for the life of
 * the tab, so a reply from the other person never appeared until a reload (the
 * app polls its open thread every 15 seconds). This re-reads the thread that is
 * on screen on the same beat.
 *
 * WHEN IT RUNS
 *   - every 15s while the tab is visible, and once more on window focus and when
 *     the tab becomes visible again (the visitor coming back is exactly when a
 *     reply is waiting);
 *   - never while the tab is hidden (the timer is cleared, not just ignored);
 *   - only for the thread whose pane is actually showing. No pane (another
 *     route) or a pane CSS has hidden (phone width, list showing) is "closed";
 *   - 🔑 AND THE MESSAGE RAIL'S, WHILE IT IS OPEN, which shows one person's
 *     thread over any page. Opening it reads that person at once, not a beat
 *     later: before this the rail painted only what a Deals visit had already
 *     loaded, so a chat that existed said "No messages yet" (found in real use, 2026-10-15).
 *     A rail on someone with no thread yet reads nothing. Over the Deals page
 *     the pane under it is read as before (dealsReadState.ts tells the server
 *     nothing about it until the rail closes), unless both show one person;
 *   - never two reads at once: the rail's thread, then the pane's;
 *   - and once more the moment the counter sheet closes, when a beat under it
 *     was deferred (below).
 *
 * 🔑 THE OTHER SIDE'S OFFER, COUNTER, ACCEPT AND DECLINE LAND ON THE BEAT TOO
 * (staging, 2026-10-21: with the other side's thread open, a new offer, a
 * counter and a decline never painted in 60 s while a message landed in 8 s).
 * The events read writes only the pair's messages, each chain's history and
 * tracking, and the writers' offer id (__gbzOfferId) onto the open rows; the
 * card's state word, gold flag, CTA, stepper, tab, and both sides come only
 * from the LIST route's rows (mapDealThread), and a deal on a new listing has
 * no row at all. So each beat compares the events body with what the list last
 * said about the open person (floorTransport listIsStale: no row for a chain,
 * or a head whose id, status or updatedAt differs) and, when the list is stale,
 * reads it once and applies it FOR THAT PERSON ONLY (installListThenEvents:
 * the bridge's scoped __gbzRefreshDeals, then the same events body), inside the
 * one keep-your-place bracket: the new row gets its history and its offer id,
 * the tab follows a moved selected deal (PR 1080's rule, in the bridge), and
 * nobody else's row moves, so another person's newer line in that answer
 * cannot slide a computer's unselected pane onto them, and the read state
 * marks nobody (PR 1078/1080). The open person's own row may move up, as it
 * does for whoever sent something new on any list read. The list is never read
 * when the heads match: a message is not a reason.
 *   The hazard this closes, on this path and on the one-shot reads
 *   (installDealThread runs the same rule): before, __gbzOfferId moved to THEIR
 *   counter while the card still showed the visitor's own offer, so a Withdraw
 *   on that card targeted their counter (403 "Not your offer"), and the counter
 *   sheet was built from stale sides but posted against the new head.
 *   While the counter sheet (#counter-back) is open a stale beat applies
 *   NOTHING, not even the events: the sheet and the writers stay on the one
 *   offer the sheet shows. The paint that removes the sheet reads again at
 *   once, so what was deferred lands then, not a beat later. (A counter sent
 *   from a sheet the other side has already answered is refused by the server;
 *   the sheet stays, and the next paint after it closes brings the new terms.)
 *   A failed list read applies nothing either, for the same reason, and the next
 *   beat tries both again (the events body is not remembered as seen).
 *
 * 🔴 WHAT IT WILL NOT DO TO THE PERSON USING THE PAGE
 *   A repaint replaces #app wholesale, so a naive re-read would wipe a half-typed
 *   message, drop the focus, and send the pane back to the top mid-read. So:
 *     - an answer identical to the last one, with the list not stale, repaints NOTHING;
 *     - while a composer send is on the wire it does not read, and an answer that
 *       was started before a send began or ended, or before any other write
 *       re-installed the thread or applied a list (threadWrites.epoch), is
 *       dropped as out of date: the thread's answer, and the list's answer, each
 *       checked when it lands;
 *     - when it does repaint it puts back both composers' text, focus and caret,
 *       the page scroll, and the pane's and the rail's scroll — following the
 *       newest message only if the person was already at the bottom; and once
 *       only, after the list merge and the events install both;
 *     - it skips a beat when the visitor is typing somewhere else (a counter
 *       sheet's field), since the repaint would eat that too.
 */

import {
  installListThenEvents,
  listHeadsFor,
  listIsStale,
  readDeals,
  threadWrites,
  type ApiThreadEventsResponse,
  type DealsRead,
} from "./floorTransport";
import { MSG_INPUT_SELECTOR, RAIL_INPUT_SELECTOR, openThreadIdFromDom } from "./dealMessaging";

export const THREAD_POLL_MS = 15_000;
/** "At the bottom" with a little slack: a fractional scrollTop is normal. */
const AT_BOTTOM_SLACK_PX = 48;
/** The counter sheet, painted by renderShellExtras while counterFor names a deal (app.js). */
const COUNTER_SHEET_SELECTOR = "#counter-back";

/** Where one surface keeps its lines and its composer. */
interface Surface {
  scroll: string;
  input: string;
}
const PANE: Surface = { scroll: ".thread-view .thread-scroll", input: MSG_INPUT_SELECTOR };
const RAIL: Surface = { scroll: "#msgrail-msgs", input: RAIL_INPUT_SELECTOR };

/** A thread on screen and the surface showing it. */
interface Target {
  threadId: string;
  surface: Surface;
}

interface RailWindow extends Window {
  __gbzMsgRailFor?: () => string | null;
  __gbzThreadIdForHandle?: (handle: string) => string | null;
  rerenderKeepScroll?: () => void;
}

/** The counterpart a thread key names: d:<id>:<listing> and p:<id> both carry it. */
const personOf = (threadId: string): string => threadId.split(":")[1] ?? threadId;

export function installDealThreadPoll(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
  { intervalMs = THREAD_POLL_MS }: { intervalMs?: number } = {},
): () => void {
  const doc = win.document;
  const writes = threadWrites(win);
  let timer: number | null = null;
  let reading = false;
  /* The last answer per thread, as text. Same text, with the list not stale, =
     nothing changed = no repaint. */
  const lastSeen = new Map<string, string>();

  const visible = (): boolean => doc.visibilityState !== "hidden";

  /* The thread whose pane is showing, or null. The pane can be in the DOM and
     hidden by the stylesheet (a phone showing the list), which is closed as far
     as the visitor is concerned. */
  const paneThread = (): string | null => {
    const view = doc.querySelector(".thread-view") as HTMLElement | null;
    if (!view) return null;
    try {
      if (win.getComputedStyle(view).display === "none") return null;
    } catch {
      /* no layout engine to ask — treat as open */
    }
    return openThreadIdFromDom(doc);
  };

  /* The rail's thread while the rail is open, else null (closed, or open on
     someone who has no thread yet). Open means the node AND the binding: two of
     its closes clear msgRailFor a task before the render that removes the node. */
  const railThread = (): string | null => {
    if (!doc.querySelector("#msgrail-back")) return null;
    const w = win as RailWindow;
    const handle = w.__gbzMsgRailFor?.() ?? null;
    return handle ? w.__gbzThreadIdForHandle?.(handle) ?? null : null;
  };

  /* What is on screen, rail first. One read covers every row of a person
     (installThreadEvents), so a pane showing the rail's person is not read twice. */
  const openTargets = (): Target[] => {
    const rail = railThread();
    const pane = paneThread();
    const out: Target[] = [];
    if (rail) out.push({ threadId: rail, surface: RAIL });
    if (pane && !(rail && personOf(rail) === personOf(pane))) out.push({ threadId: pane, surface: PANE });
    return out;
  };
  const stillOpen = ({ threadId, surface }: Target): boolean =>
    openTargets().some((t) => t.threadId === threadId && t.surface === surface);

  const typingElsewhere = (): boolean => {
    const el = doc.activeElement as HTMLElement | null;
    if (!el || el.id === PANE.input.slice(1) || el.id === RAIL.input.slice(1)) return false;
    return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable;
  };

  /* The counter sheet is open: a stale list is left for the paint that closes it (header). */
  const counterSheetOpen = (): boolean => !!doc.querySelector(COUNTER_SHEET_SELECTOR);
  /* A stale beat was deferred under the sheet, and is owed when the sheet closes. */
  let deferredUnderSheet = false;

  /* One surface's place: where its lines are scrolled, what is typed in its box. */
  const placeOf = (s: Surface) => {
    const pane = doc.querySelector(s.scroll) as HTMLElement | null;
    const box = doc.querySelector(s.input) as HTMLInputElement | null;
    const hadFocus = !!box && doc.activeElement === box;
    return {
      s,
      wasAtBottom: pane ? pane.scrollHeight - pane.scrollTop - pane.clientHeight <= AT_BOTTOM_SLACK_PX : false,
      top: pane ? pane.scrollTop : 0,
      typed: box?.value ?? "",
      hadFocus,
      caret: hadFocus ? [box!.selectionStart, box!.selectionEnd] : null,
    };
  };
  const putBack = (p: ReturnType<typeof placeOf>): void => {
    const nextPane = doc.querySelector(p.s.scroll) as HTMLElement | null;
    if (nextPane) nextPane.scrollTop = p.wasAtBottom ? nextPane.scrollHeight : p.top;
    const nextBox = doc.querySelector(p.s.input) as HTMLInputElement | null;
    if (nextBox) {
      if (p.typed && !nextBox.value) nextBox.value = p.typed;
      if (p.hadFocus) {
        nextBox.focus({ preventScroll: true });
        if (p.caret && p.caret[0] !== null && p.caret[1] !== null) nextBox.setSelectionRange(p.caret[0], p.caret[1]);
      }
    }
  };

  /* Paint the new answer without costing the person their place, on either
     surface: a repaint redraws both when the rail is open over Deals. With a
     list read (the open person's offers moved), the list goes on first, for
     that person only, then the same events body onto the rows it left
     (installListThenEvents), and the place is put back once, after both. */
  const installKeepingPlace = ({ threadId, surface }: Target, body: ApiThreadEventsResponse, list: DealsRead | null): string => {
    const places = [placeOf(PANE), placeOf(RAIL)];
    const pageY = win.scrollY;

    const { installId } = installListThenEvents(win, threadId, myUserId, body, list);
    /* The bridge repaints only on #/deals and #/inbox (__gbzInstallDealThread).
       A rail open over any other page shows the same rows and needs the paint. */
    const h = win.location.hash;
    if (surface === RAIL && h !== "#/deals" && h !== "#/inbox") (win as RailWindow).rerenderKeepScroll?.();

    try {
      win.scrollTo({ top: pageY, behavior: "instant" as ScrollBehavior });
    } catch {
      /* jsdom has no scrolling; a real browser always does */
    }
    for (const p of places) putBack(p);
    return installId;
  };

  /* The rail thread read since the rail last opened, so a repaint (the poll's
     own included) does not read it again. Set when a read STARTS, so a failed
     read waits for the beat instead of retrying on every paint. */
  let railRead: string | null = null;
  /* Set by the teardown, so a read already out installs nothing and starts
     nothing after it. */
  let stopped = false;

  /* Out of date the moment anything else wrote the thread or applied a list, or
     the visitor sent, while an answer was in flight; hidden again or closed means
     nobody is looking. The next beat reads fresh. Asked of the thread's answer and
     of the list's, each when it lands. */
  const outOfDate = (startedAt: number, target: Target): boolean =>
    stopped || writes.epoch !== startedAt || writes.inFlight > 0 || !visible() || !stillOpen(target);

  const read = async (target: Target): Promise<void> => {
    if (stopped || !visible() || writes.inFlight > 0 || typingElsewhere() || !stillOpen(target)) return;
    const { threadId, surface } = target;
    if (surface === RAIL) railRead = threadId;
    const startedAt = writes.epoch;
    const res = await authFetch(`/api/deal-threads/${encodeURIComponent(threadId)}/events`);
    if (!res.ok) return;
    const body = (await res.json()) as ApiThreadEventsResponse;
    if (outOfDate(startedAt, target)) return;
    const seen = JSON.stringify(body);
    const stale = listIsStale(body, listHeadsFor(win, threadId));
    if (!stale && lastSeen.get(threadId) === seen) return;

    let list: DealsRead | null = null;
    if (stale) {
      // The sheet is built from the row's sides and posts against the row's
      // offer id: both move together or not at all (header).
      if (counterSheetOpen()) {
        deferredUnderSheet = true;
        return;
      }
      list = await readDeals(win, authFetch, myUserId);
      // A failed list read applies nothing, the events included, and is not
      // remembered as seen: the next beat tries both again.
      if (!list || outOfDate(startedAt, target)) return;
      if (counterSheetOpen()) {
        deferredUnderSheet = true;
        return;
      }
    }
    deferredUnderSheet = false;
    lastSeen.set(threadId, seen);
    const installId = installKeepingPlace(target, body, list);
    if (installId !== threadId) lastSeen.set(installId, seen);
  };

  const tick = async (again = false): Promise<void> => {
    if (stopped || reading || !visible() || writes.inFlight > 0 || typingElsewhere()) return;
    const targets = openTargets();
    if (!targets.length) return;

    reading = true;
    try {
      for (const target of targets) {
        try {
          await read(target);
        } catch {
          // Best-effort, like every other read in this lane: a failed poll leaves
          // the thread as it was and the next beat tries again.
        }
      }
    } finally {
      reading = false;
      // A rail opened while this was out gets its read now, not a beat later.
      // Once: a read that could not start leaves the rest to the beat.
      const rail = railThread();
      if (!again && rail && rail !== railRead) void tick(true);
    }
  };

  /* Every rail open is a render of #app (its openers re-render), so an observer
     sees it, and a rail just opened, or now on someone else, is read at once. A
     closed rail forgets what it read, so the next open reads again. The paint
     that removes the counter sheet reads at once too, when a stale beat was
     deferred under it. */
  let sheetWasOpen = counterSheetOpen();
  const onPaint = (): void => {
    const rail = railThread();
    if (!doc.querySelector("#msgrail-back")) railRead = null;
    else if (rail && rail !== railRead) void tick();
    const sheetOpen = counterSheetOpen();
    if (sheetWasOpen && !sheetOpen && deferredUnderSheet) void tick();
    sheetWasOpen = sheetOpen;
  };
  const app = doc.getElementById("app");
  const observer = app ? new MutationObserver(onPaint) : null;
  observer?.observe(app!, { childList: true, subtree: true });

  const start = (): void => {
    if (timer === null) timer = win.setInterval(() => void tick(), intervalMs);
  };
  const stop = (): void => {
    if (timer !== null) {
      win.clearInterval(timer);
      timer = null;
    }
  };

  const onVisibility = (): void => {
    if (visible()) {
      start();
      void tick();
    } else {
      stop();
    }
  };
  const onFocus = (): void => {
    void tick();
  };

  if (visible()) start();
  doc.addEventListener("visibilitychange", onVisibility);
  win.addEventListener("focus", onFocus);
  return () => {
    stopped = true;
    stop();
    observer?.disconnect();
    doc.removeEventListener("visibilitychange", onVisibility);
    win.removeEventListener("focus", onFocus);
  };
}
